import React, { useEffect, useState } from 'react';
import { 
  AlertCircle, 
  FileCheck, 
  Upload, 
  Loader2, 
  Trash2, 
  ShieldCheck,
  FileText,
  Video,
  XCircle,
  CheckCircle2,
  Clock
} from 'lucide-react';
import { Button } from '../ui/button';
import { useMedicalDocuments, DoctorDocumentType } from '../../hooks/useMedicalDocuments';
import { cn } from '@/lib/utils';
import { validateConfirmationVideoFile } from '@/lib/confirmationVideo';
import { isCameraRecordingSupported } from '@/lib/confirmationVideoRecorder';
import {
  ConfirmationVideoRecorder,
  ConfirmationVideoSourceToggle,
  type ConfirmationVideoSourceMode,
} from '@/components/documents/ConfirmationVideoRecorder';

interface MedicalCredentialsUploadProps {
  doctorId: string;
  onAllDocumentsUploaded?: () => void;
}

export const MedicalCredentialsUpload: React.FC<MedicalCredentialsUploadProps> = ({
  doctorId,
  onAllDocumentsUploaded,
}) => {
  const { 
    uploadDocument, 
    deleteDocument, 
    fetchDocuments, 
    documents, 
  } = useMedicalDocuments(doctorId);

  // Arquivos selecionados localmente (antes de fazer upload)
  const [selectedFiles, setSelectedFiles] = useState<Record<DoctorDocumentType, File | null>>({
    [DoctorDocumentType.CRM]: null,
    [DoctorDocumentType.SPECIALTY_CERTIFICATE]: null,
    [DoctorDocumentType.INSCRIPTION_PROOF]: null,
    [DoctorDocumentType.CONFIRMATION_VIDEO]: null,
    [DoctorDocumentType.DIGITAL_CERTIFICATE]: null,
  });

  // Estados de upload e erro isolados por tipo de documento
  const [uploadStates, setUploadStates] = useState<Record<DoctorDocumentType, { isLoading: boolean; error: string | null }>>({
    [DoctorDocumentType.CRM]: { isLoading: false, error: null },
    [DoctorDocumentType.SPECIALTY_CERTIFICATE]: { isLoading: false, error: null },
    [DoctorDocumentType.INSCRIPTION_PROOF]: { isLoading: false, error: null },
    [DoctorDocumentType.CONFIRMATION_VIDEO]: { isLoading: false, error: null },
    [DoctorDocumentType.DIGITAL_CERTIFICATE]: { isLoading: false, error: null },
  });

  const [videoSourceMode, setVideoSourceMode] = useState<ConfirmationVideoSourceMode>('upload');
  const recordingSupported = isCameraRecordingSupported();

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  const applyConfirmationVideoFile = async (file: File, type: DoctorDocumentType) => {
    setUploadStates((prev) => ({
      ...prev,
      [type]: { isLoading: true, error: null },
    }));
    const videoValidation = await validateConfirmationVideoFile(file);
    if (!videoValidation.valid) {
      setUploadStates((prev) => ({
        ...prev,
        [type]: { isLoading: false, error: videoValidation.error },
      }));
      return;
    }
    setSelectedFiles((prev) => ({ ...prev, [type]: file }));
    setUploadStates((prev) => ({
      ...prev,
      [type]: { isLoading: false, error: null },
    }));
  };

  const validateFile = (file: File, type: DoctorDocumentType): { valid: boolean; error?: string } => {
    const maxSize = 500 * 1024 * 1024; // 500MB conforme requisito
    if (file.size > maxSize) {
      return { valid: false, error: 'Arquivo muito grande. Máximo 500MB.' };
    }

    if (type === DoctorDocumentType.DIGITAL_CERTIFICATE) {
      const maxCert = 5 * 1024 * 1024;
      if (file.size > maxCert) {
        return { valid: false, error: 'Certificado digital muito grande. Máximo 5MB.' };
      }
      if (!/\.(pfx|p12|cer|crt|pem)$/i.test(file.name)) {
        return { valid: false, error: 'Formato inválido. Use .pfx, .p12, .cer, .crt ou .pem.' };
      }
      return { valid: true };
    }

    const allowedTypes = ['application/pdf', 'image/png', 'image/jpeg'];
    if (!allowedTypes.includes(file.type)) {
      return { valid: false, error: 'Formato inválido. Aceita PDF, PNG ou JPG.' };
    }

    return { valid: true };
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>, type: DoctorDocumentType) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (type === DoctorDocumentType.CONFIRMATION_VIDEO) {
      await applyConfirmationVideoFile(file, type);
      e.target.value = '';
      return;
    }

    const validation = validateFile(file, type);
    if (!validation.valid) {
      setUploadStates(prev => ({
        ...prev,
        [type]: { isLoading: false, error: validation.error || 'Arquivo inválido' }
      }));
      e.target.value = '';
      return;
    }

    setSelectedFiles(prev => ({
      ...prev,
      [type]: file
    }));
    setUploadStates(prev => ({
      ...prev,
      [type]: { isLoading: false, error: null }
    }));
    
    e.target.value = '';
  };

  const handleUpload = async (type: DoctorDocumentType) => {
    const file = selectedFiles[type];
    if (!file) return;

    setUploadStates(prev => ({
      ...prev,
      [type]: { isLoading: true, error: null }
    }));

    try {
      const success = await uploadDocument(file, type);
      if (success) {
        setSelectedFiles(prev => ({
          ...prev,
          [type]: null
        }));
        setUploadStates(prev => ({
          ...prev,
          [type]: { isLoading: false, error: null }
        }));
      } else {
        setUploadStates(prev => ({
          ...prev,
          [type]: { isLoading: false, error: 'Falha ao processar o upload do arquivo.' }
        }));
      }
    } catch (err: any) {
      setUploadStates(prev => ({
        ...prev,
        [type]: { isLoading: false, error: err.message || 'Erro inesperado' }
      }));
    }
  };

  const handleCancelSelection = (type: DoctorDocumentType) => {
    setSelectedFiles(prev => ({
      ...prev,
      [type]: null
    }));
    setUploadStates(prev => ({
      ...prev,
      [type]: { isLoading: false, error: null }
    }));
  };

  const handleDelete = async (documentId: string, type: DoctorDocumentType) => {
    if (!window.confirm('Tem certeza que deseja remover este documento?')) return;

    setUploadStates(prev => ({
      ...prev,
      [type]: { isLoading: true, error: null }
    }));

    try {
      const success = await deleteDocument(documentId);
      if (success) {
        setUploadStates(prev => ({
          ...prev,
          [type]: { isLoading: false, error: null }
        }));
      } else {
        setUploadStates(prev => ({
          ...prev,
          [type]: { isLoading: false, error: 'Erro ao remover o documento do servidor.' }
        }));
      }
    } catch (err: any) {
      setUploadStates(prev => ({
        ...prev,
        [type]: { isLoading: false, error: err.message || 'Erro ao deletar' }
      }));
    }
  };

  const getDocumentByType = (type: DoctorDocumentType) => {
    return documents.find(doc => doc.documentType === type);
  };

  const documentSections = [
    { 
      type: DoctorDocumentType.CRM, 
      label: 'CRM Oficial', 
      description: 'Cópia legível do seu CRM profissional.',
      accept: '.pdf,.png,.jpg,.jpeg',
    },
    { 
      type: DoctorDocumentType.SPECIALTY_CERTIFICATE, 
      label: 'Certificado de Especialidade', 
      description: 'Diploma ou certificado de conclusão de especialização.',
      accept: '.pdf,.png,.jpg,.jpeg',
    },
    { 
      type: DoctorDocumentType.INSCRIPTION_PROOF, 
      label: 'Comprovante de Inscrição', 
      description: 'Comprovante de regularidade no conselho de medicina.',
      accept: '.pdf,.png,.jpg,.jpeg',
    },
    {
      type: DoctorDocumentType.CONFIRMATION_VIDEO,
      label: 'Vídeo de confirmação',
      description: 'Grave um vídeo de exatamente 3 segundos (MP4/WEBM, até 20MB) para validação de identidade.',
      accept: 'video/mp4,video/webm,video/quicktime,.mp4,.webm,.mov',
    },
  ];

  const allUploaded = documentSections.every(s => !!getDocumentByType(s.type));

  useEffect(() => {
    if (allUploaded && onAllDocumentsUploaded) {
      onAllDocumentsUploaded();
    }
  }, [allUploaded, onAllDocumentsUploaded]);

  return (
    <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6 shadow-sm">
      <div className="flex items-start justify-between mb-6">
        <div className="flex items-start gap-3">
          <ShieldCheck className="w-6 h-6 text-teal-600 dark:text-teal-400 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Verificação de Credenciais Médicas</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              CRM, especialidade, inscrição e vídeo. A assinatura de receitas é na Memed (Bird ID /
              VIDAS / A3 cloud), não neste upload.
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        {documentSections.map((section) => {
          const doc = getDocumentByType(section.type);
          const localFile = selectedFiles[section.type];
          const state = uploadStates[section.type];
          const isVideo = section.type === DoctorDocumentType.CONFIRMATION_VIDEO;
          const SectionIcon = isVideo ? Video : FileText;
          
          return (
            <div 
              key={section.type} 
              className={`p-4 border rounded-lg transition-all duration-200 ${
                doc && doc.status === 'APPROVED'
                  ? 'bg-green-50/50 dark:bg-green-900/10 border-green-200 dark:border-green-800/50' 
                  : doc && doc.status === 'REJECTED'
                    ? 'bg-red-50/50 dark:bg-red-900/10 border-red-200 dark:border-red-800/50'
                    : doc
                      ? 'bg-amber-50/50 dark:bg-amber-900/10 border-amber-200 dark:border-amber-800/50'
                      : localFile
                        ? 'bg-blue-50/30 dark:bg-blue-900/10 border-blue-200 dark:border-blue-800/50 shadow-xs'
                        : 'bg-gray-50 dark:bg-gray-800/50 border-gray-200 dark:border-gray-700'
              }`}
            >
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-3 min-w-0">
                  <div className={`p-2 rounded-full ${
                    doc && doc.status === 'APPROVED' 
                      ? 'bg-green-100 dark:bg-green-900/30' 
                      : doc && doc.status === 'REJECTED'
                        ? 'bg-red-100 dark:bg-red-900/30'
                        : doc
                          ? 'bg-amber-100 dark:bg-amber-900/30'
                          : localFile 
                            ? 'bg-blue-100 dark:bg-blue-900/30' 
                            : 'bg-gray-200 dark:bg-gray-700'
                  }`}>
                    <SectionIcon className={`w-5 h-5 ${
                      doc && doc.status === 'APPROVED'
                        ? 'text-green-600 dark:text-green-400'
                        : doc && doc.status === 'REJECTED'
                          ? 'text-red-600 dark:text-red-400'
                          : doc
                            ? 'text-amber-600 dark:text-amber-400'
                            : localFile
                              ? 'text-blue-600 dark:text-blue-400'
                              : 'text-gray-500 dark:text-gray-400'
                    }`} />
                  </div>
                  <div className="min-w-0">
                    <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{section.label}</h4>
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{section.description}</p>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  {doc && doc.status !== 'REJECTED' ? (
                    <div className={cn(
                      "flex items-center gap-2 px-2.5 py-1.5 rounded-md",
                      doc.status === 'APPROVED' 
                        ? 'bg-green-100/80 dark:bg-green-900/30 text-green-800 dark:text-green-300' 
                        : 'bg-amber-100/80 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300'
                    )}>
                      {doc.status === 'APPROVED' ? (
                        <CheckCircle2 className="w-3.5 h-3.5 text-green-700 dark:text-green-400" />
                      ) : (
                        <Clock className="w-3.5 h-3.5 text-amber-700 dark:text-amber-400 animate-pulse" />
                      )}
                      <span className="text-xs font-semibold">
                        {doc.status === 'APPROVED' ? 'Aprovado' : 'Em Análise'}
                      </span>
                      {doc.status !== 'APPROVED' && (
                        <button
                          onClick={() => handleDelete(doc.id, section.type)}
                          disabled={state.isLoading}
                          className="p-0.5 text-gray-400 hover:text-red-600 transition-colors ml-1"
                          title="Excluir documento salvo"
                        >
                          {state.isLoading ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          ) : (
                            <Trash2 className="w-3.5 h-3.5" />
                          )}
                        </button>
                      )}
                    </div>
                  ) : localFile ? (
                    <div className="flex items-center gap-1.5">
                      <Button
                        type="button"
                        size="sm"
                        variant="default"
                        onClick={() => handleUpload(section.type)}
                        disabled={state.isLoading}
                        className="bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold px-3 py-1.5 h-8 gap-1 shadow-sm"
                      >
                        {state.isLoading ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                          <Upload className="w-3.5 h-3.5" />
                        )}
                        Enviar
                      </Button>
                      <button
                        onClick={() => handleCancelSelection(section.type)}
                        disabled={state.isLoading}
                        className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
                        title="Cancelar seleção"
                      >
                        <XCircle className="w-4 h-4" />
                      </button>
                    </div>
                  ) : isVideo ? (
                    <div className="w-full basis-full mt-2 space-y-3">
                      <ConfirmationVideoSourceToggle
                        mode={videoSourceMode}
                        onChange={setVideoSourceMode}
                        recordingSupported={recordingSupported}
                        disabled={state.isLoading}
                      />
                      {!recordingSupported && (
                        <p className="text-[10px] text-amber-700">
                          Gravação pela câmera indisponível neste navegador — use o upload.
                        </p>
                      )}
                      {videoSourceMode === 'upload' ? (
                        <label className="cursor-pointer inline-flex">
                          <div className="flex items-center gap-1.5 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 px-3 py-1.5 rounded-md text-xs font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors shadow-sm">
                            <Upload className="w-3.5 h-3.5 text-gray-500 dark:text-gray-400" />
                            {doc && doc.status === 'REJECTED' ? 'Reenviar arquivo' : 'Selecionar arquivo'}
                          </div>
                          <input
                            type="file"
                            className="hidden"
                            accept={section.accept}
                            onChange={(e) => handleFileChange(e, section.type)}
                            disabled={state.isLoading}
                          />
                        </label>
                      ) : (
                        <ConfirmationVideoRecorder
                          disabled={state.isLoading}
                          onCancel={() => setVideoSourceMode('upload')}
                          onFileReady={(file) => {
                            void applyConfirmationVideoFile(file, section.type);
                          }}
                        />
                      )}
                    </div>
                  ) : (
                    <label className="cursor-pointer">
                      <div className="flex items-center gap-1.5 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 px-3 py-1.5 rounded-md text-xs font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors shadow-sm">
                        <Upload className="w-3.5 h-3.5 text-gray-500 dark:text-gray-400" />
                        {doc && doc.status === 'REJECTED' ? 'Reenviar' : 'Selecionar'}
                      </div>
                      <input 
                        type="file" 
                        className="hidden" 
                        accept={section.accept}
                        onChange={(e) => handleFileChange(e, section.type)}
                        disabled={state.isLoading}
                      />
                    </label>
                  )}
                </div>
              </div>
              
              {doc && (
                <div className="mt-2 pl-11 space-y-1">
                  <p className="text-[10px] text-gray-500 dark:text-gray-400 truncate max-w-[400px]">
                    {doc.originalFileName} • {new Date(doc.createdAt).toLocaleDateString('pt-BR')}
                    {doc.status === 'APPROVED' && ' • ✓ Documento Aprovado'}
                    {doc.status === 'PENDING_REVIEW' && ' • ⏳ Em Análise'}
                    {doc.status === 'REJECTED' && ' • ❌ Recusado'}
                  </p>
                  {doc.status === 'REJECTED' && doc.rejectionReason && (
                    <div className="bg-red-50 text-red-700 text-[10px] p-2 rounded-md border border-red-100 max-w-[500px]">
                      <span className="font-semibold">Motivo da Recusa:</span> {doc.rejectionReason}
                    </div>
                  )}
                </div>
              )}

              {!doc && localFile && (
                <div className="mt-2 pl-11 flex items-center gap-1.5">
                  <span className="text-[10px] bg-blue-100/50 text-blue-700 px-2 py-0.5 rounded-sm font-medium">
                    Pronto para enviar
                  </span>
                  <p className="text-[10px] text-blue-600 truncate max-w-[300px] italic">
                    {localFile.name} • {(localFile.size / (1024 * 1024)).toFixed(2)} MB
                  </p>
                </div>
              )}

              {state.error && (
                <div className="mt-2 pl-11 flex items-center gap-1.5 text-red-600 animate-fade-in">
                  <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
                  <p className="text-[10px] font-medium">{state.error}</p>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {allUploaded && (
        <div className="mt-6 p-4 bg-teal-50 dark:bg-teal-900/10 border border-teal-100 dark:border-teal-800/50 rounded-lg flex items-center gap-3">
          <div className="bg-teal-100 dark:bg-teal-900/30 p-2 rounded-full">
            <FileCheck className="w-5 h-5 text-teal-600 dark:text-teal-400" />
          </div>
          <div>
            <p className="text-sm font-semibold text-teal-900 dark:text-teal-200">Documentação Completa</p>
            <p className="text-xs text-teal-700 dark:text-teal-400">Seus dados estão sendo analisados. Você será notificado quando for aprovado.</p>
          </div>
        </div>
      )}

      <div className="mt-6 p-4 bg-amber-50 dark:bg-amber-900/10 rounded-lg border border-amber-200 dark:border-amber-800/50">
        <p className="text-[10px] text-amber-800 dark:text-amber-300 font-bold uppercase tracking-wider mb-2">Segurança e Privacidade:</p>
        <p className="text-[10px] text-amber-700 dark:text-amber-400/90 leading-relaxed">
          Seus documentos são protegidos por criptografia de ponta a ponta. Apenas administradores autorizados têm acesso às chaves de descriptografia para verificação. O armazenamento segue rigorosamente as normas da LGPD.
        </p>
      </div>
    </div>
  );
};
